// Navegación entre módulos: app de calle (/vender), panel (/panel) con su vista de vendedor, proveedores, soporte y portal de agencias.
// Lógica pura y acceso seguro al almacenamiento del navegador (puede faltar en vista previa, modo privado o con datos bloqueados).

export const ROLES_PANEL = ["Dirección", "Coordinador", "Vendedor", "RH", "Soporte", "Agencia", "Guía"] as const;
export type RolPanel = (typeof ROLES_PANEL)[number];
export type Contexto = "vender" | "chofer" | "panel";
export type Modulo = "vender" | "chofer" | "panel" | "vista-vendedor" | "proveedores" | "soporte" | "agencias";
export type SeccionInicial = "Hoy" | "Soporte" | "Proveedores";

/** El rol sigue entre visitas (localStorage). La última vista del panel dura lo que la pestaña (sessionStorage). */
export const CLAVE_ROL = "panel-rol-v1";
export const CLAVE_VISTA = "panel-vista-v1";
/** Aviso de ventana: el panel cambió de rol y la barra de navegación debe actualizarse. */
export const EVENTO_ROL = "panel-rol-cambio";
export const RUTA_VOLVER = "/panel";

const RUTAS: Record<Modulo, { etiqueta: string; detalle: string; ruta: string }> = {
  vender: { etiqueta: "Vender", detalle: "App de calle: venta, cobro y escáner", ruta: "/vender" },
  chofer: { etiqueta: "Choferes", detalle: "Salidas asignadas, pasajeros y abordaje", ruta: "/chofer" },
  panel: { etiqueta: "Panel", detalle: "Hoy y todas las secciones", ruta: "/panel" },
  "vista-vendedor": { etiqueta: "Vista de vendedor", detalle: "Lo que ve un vendedor en el panel", ruta: "/panel" },
  proveedores: { etiqueta: "Proveedores", detalle: "Restaurantes y caballerizas: cuentas, lugares y pagos", ruta: "/panel" },
  soporte: { etiqueta: "Soporte", detalle: "Solicitudes y ayuda", ruta: "/panel" },
  agencias: { etiqueta: "Portal de agencias", detalle: "Reservas y convenios de agencias", ruta: "/agencias" },
};

export type Destino = { modulo: Modulo; etiqueta: string; detalle: string; ruta: string; rol?: RolPanel; seccion?: SeccionInicial };

export function esRolPanel(x: unknown): x is RolPanel {
  return typeof x === "string" && (ROLES_PANEL as readonly string[]).includes(x);
}

/**
 * Módulos a los que entra cada rol. Sin sesión se muestra la lista completa (es la vista por defecto del panel).
 * Cada destino dice qué rol y qué sección abrir al entrar; "Volver al panel" no se lista aquí (restaura la última vista).
 */
export function modulosDe(rol: RolPanel | null): Destino[] {
  const d = (modulo: Modulo, extra: Partial<Destino> = {}): Destino => ({ modulo, ...RUTAS[modulo], ...extra });
  if (rol === "Agencia") return [d("agencias")];
  if (rol === "Guía") return [d("panel", { etiqueta: "Mi día", detalle: "Tus salidas y escaneo de boletos", seccion: "Hoy" })];
  if (rol === "RH" || rol === "Soporte") return [d("panel", { seccion: "Hoy" }), d("soporte", { seccion: "Soporte" })];
  if (rol === "Vendedor") return [d("vender"), d("panel", { etiqueta: "Mi vista", detalle: "Tu vista de vendedor", seccion: "Hoy" }), d("soporte", { seccion: "Soporte" })];
  return [
    d("vender"),
    d("chofer"),
    d("vista-vendedor", { rol: "Vendedor", seccion: "Hoy" }),
    d("panel", { seccion: "Hoy" }),
    d("proveedores", { seccion: "Proveedores" }),
    d("soporte", { seccion: "Soporte" }),
  ];
}

/** Ruta de un módulo. En la vista previa estática las rutas son relativas a la raíz del sitio (<base> de scripts/vista-previa.mjs). */
export function hrefDe(ruta: string, exportado: boolean): string {
  return exportado ? `${ruta.replace(/^\//, "")}/` : ruta;
}

// ───────── Almacenamiento (todo con try/catch: puede no existir)
type Alm = { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void };
function almacen(tipo: "local" | "sesion"): Alm | null {
  try {
    if (typeof window === "undefined") return null;
    return tipo === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function leerRol(): RolPanel | null {
  try {
    const v = almacen("local")?.getItem(CLAVE_ROL);
    return esRolPanel(v) ? v : null;
  } catch {
    return null;
  }
}

/** Guarda el rol que se ve en el panel (o lo borra al salir) y avisa a la barra de navegación. */
export function guardarRol(rol: RolPanel | null): void {
  try {
    if (rol) almacen("local")?.setItem(CLAVE_ROL, rol);
    else almacen("local")?.removeItem(CLAVE_ROL);
  } catch {
    /* sin almacenamiento: la sesión dura lo que la página */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENTO_ROL));
}

export function guardarVista(vista: unknown): void {
  try {
    almacen("sesion")?.setItem(CLAVE_VISTA, JSON.stringify(vista));
  } catch {
    /* sin almacenamiento */
  }
}

export function olvidarVista(): void {
  try {
    almacen("sesion")?.removeItem(CLAVE_VISTA);
  } catch {
    /* sin almacenamiento */
  }
}

export function leerVistaGuardada(): unknown {
  try {
    const s = almacen("sesion")?.getItem(CLAVE_VISTA);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export type VistaRestaurada =
  | { tipo: "seccion"; seccion: string; sub?: string }
  | { tipo: "ficha"; clave: string; id: string | null; fijos?: Record<string, string>; pestana?: string };

/**
 * Valida la última vista guardada antes de volver a ella. Si la sección, la pestaña o la ficha ya no existen para el rol
 * actual, no se restaura (regresa null y el panel abre Hoy).
 */
export function vistaRestaurable(
  x: unknown,
  permite: { seccion: (s: string) => boolean; sub: (seccion: string, sub: string) => boolean; ficha: (clave: string) => boolean },
): VistaRestaurada | null {
  if (!x || typeof x !== "object") return null;
  const v = x as Record<string, unknown>;
  if (v.tipo === "seccion" && typeof v.seccion === "string" && permite.seccion(v.seccion)) {
    const sub = typeof v.sub === "string" && permite.sub(v.seccion, v.sub) ? v.sub : undefined;
    return sub ? { tipo: "seccion", seccion: v.seccion, sub } : { tipo: "seccion", seccion: v.seccion };
  }
  if (v.tipo === "ficha" && typeof v.clave === "string" && permite.ficha(v.clave)) {
    const fijos = v.fijos && typeof v.fijos === "object"
      ? Object.fromEntries(Object.entries(v.fijos as Record<string, unknown>).filter(([, val]) => typeof val === "string") as [string, string][])
      : undefined;
    return {
      tipo: "ficha",
      clave: v.clave,
      id: typeof v.id === "string" ? v.id : null,
      ...(fijos ? { fijos } : {}),
      ...(typeof v.pestana === "string" ? { pestana: v.pestana } : {}),
    };
  }
  return null;
}
